import { supabase } from '../supabase'

export interface GlossaryTerm {
  id: string
  term_source: string
  term_target: string
  source_language: string
  target_language: string
  definition?: string
  domain?: string
  notes?: string
  created_by: string
  created_at: string
}

export interface GlossaryMatch {
  term: GlossaryTerm
  position: number
  length: number
}

export const glossaryService = {
  async addTerm(
    termSource: string,
    termTarget: string,
    sourceLang: string,
    targetLang: string,
    definition?: string,
    domain?: string,
    notes?: string
  ): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return false
    const { error } = await supabase.from('terminology').insert({
      term_source: termSource.trim(),
      term_target: termTarget.trim(),
      source_language: sourceLang,
      target_language: targetLang,
      definition: definition || null,
      domain: domain || null,
      notes: notes || null,
      created_by: user.id,
    })
    return !error
  },

  async getTerms(
    sourceLang: string,
    targetLang: string,
    search = '',
    page = 0,
    pageSize = 50
  ): Promise<{ terms: GlossaryTerm[]; count: number }> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { terms: [], count: 0 }
    let query = supabase
      .from('terminology')
      .select('*', { count: 'exact' })
      .eq('created_by', user.id)
      .eq('source_language', sourceLang)
      .eq('target_language', targetLang)
    if (search.trim()) {
      query = query.or(
        `term_source.ilike.%${search}%,term_target.ilike.%${search}%,definition.ilike.%${search}%`
      )
    }
    const { data, count, error } = await query
      .order('term_source', { ascending: true })
      .range(page * pageSize, (page + 1) * pageSize - 1)
    if (error) return { terms: [], count: 0 }
    return { terms: (data as GlossaryTerm[]) ?? [], count: count ?? 0 }
  },

  async getAllTerms(sourceLang: string, targetLang: string): Promise<GlossaryTerm[]> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return []
    const { data } = await supabase
      .from('terminology')
      .select('*')
      .eq('created_by', user.id)
      .eq('source_language', sourceLang)
      .eq('target_language', targetLang)
      .order('term_source', { ascending: true })
    return (data as GlossaryTerm[]) ?? []
  },

  async deleteTerm(id: string): Promise<boolean> {
    const { error } = await supabase.from('terminology').delete().eq('id', id)
    return !error
  },

  async updateTerm(id: string, updates: Partial<GlossaryTerm>): Promise<boolean> {
    const { error } = await supabase.from('terminology').update(updates).eq('id', id)
    return !error
  },

  /** Find all glossary terms present in a given text */
  findMatches(text: string, terms: GlossaryTerm[]): GlossaryMatch[] {
    const matches: GlossaryMatch[] = []
    const lower = text.toLowerCase()
    for (const term of terms) {
      const needle = term.term_source.toLowerCase()
      let idx = lower.indexOf(needle)
      while (idx !== -1) {
        matches.push({ term, position: idx, length: term.term_source.length })
        idx = lower.indexOf(needle, idx + 1)
      }
    }
    return matches.sort((a, b) => a.position - b.position)
  },

  exportCSV(terms: GlossaryTerm[]): string {
    const header = 'Source Term,Target Term,Source Lang,Target Lang,Domain,Definition,Notes'
    const rows = terms.map(t =>
      [t.term_source, t.term_target, t.source_language, t.target_language,
       t.domain || '', t.definition || '', t.notes || '']
        .map(v => `"${v.replace(/"/g, '""')}"`)
        .join(',')
    )
    return [header, ...rows].join('\n')
  },
}
